import { types } from '@concordia/super-sdk'
import DataPoint, { DataPointProps } from 'components/common/DataPoint'
import React, { useMemo, useState } from 'react'
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer, YAxis } from 'recharts'
import { formatCurrency, formatDateExact, formatDateSimple } from 'toolbox/format'
import { SimpleSpinner } from './SimpleSpinner'
import TimeFilterTabs from 'components/common/TimeFilterTabs'
import { timeTabsPrice } from '../../state/slices/app/brokerHistories'
import { isEmptyOrNil } from 'toolbox/account'
import { useAppSelector } from 'state/hooks'
import { selectDetailsOpen } from 'state/slices/ui/form'
import { Label } from './Label'

export function OneLinePrice({
  data,
  isLoaded,
  price
}: {
  data: types.HistoricalPrice[]
  isLoaded: boolean
  price: number
}) {
  const [activeData, setActiveData] = useState(null)
  const activePrice = activeData ? activeData.usd : null
  const [activeTab, setActiveTab] = useState('1M')
  const numberOfDays = timeTabsPrice.find((tab) => tab.label === activeTab).days
  const filteredData = useMemo(() => {
    return data.filter((d) => d.timestamp * 1000 > Date.now() - numberOfDays * 24 * 60 * 60 * 1000)
  }, [data, numberOfDays])
  const isDetailsOpen = useAppSelector(selectDetailsOpen)

  const handleMouseMove = (data) => {
    if (data.activePayload) {
      setActiveData(data.activePayload[0].payload)
    }
  }

  const handleMouseLeave = () => {
    setActiveData(null)
  }
  const chartData = useMemo(
    () =>
      filteredData.map((item) => ({
        time: formatDateSimple(item.timestamp),
        exactTime: formatDateExact(item.timestamp),
        usd: parseFloat(item.usd)
      })),
    [filteredData]
  )

  const numericData = useMemo(
    () => filteredData.map((d) => ({ ...d, usd: parseFloat(d.usd) })),
    [filteredData]
  )

  const hoverIndex = useMemo(
    () => numericData.findIndex((d) => d.usd === activePrice),
    [numericData, activePrice]
  )

  const slicedNumericData = useMemo(
    () => numericData.slice(0, hoverIndex + 1),
    [numericData, hoverIndex]
  )

  const priceDetails = useMemo(() => {
    const initialPrice = slicedNumericData[0]?.usd || numericData[0]?.usd
    const finalPrice =
      slicedNumericData[slicedNumericData.length - 1]?.usd ||
      numericData[numericData.length - 1]?.usd ||
      0
    const priceChange = finalPrice - initialPrice
    const percentageChange = initialPrice > 0 ? priceChange / initialPrice : 0
    return {
      initialPrice,
      finalPrice,
      priceChange,
      percentageChange,
      isChangePositive: percentageChange > 0
    }
  }, [slicedNumericData, numericData])
  const { percentageChange, isChangePositive } = priceDetails

  const priceDataPoint: DataPointProps = {
    label: 'Price',
    value: activeData ? formatCurrency(activeData.usd) : formatCurrency(price),
    tooltip: 'Current Asset Price (USD)',
    subValue: {
      subVal: percentageChange,
      isPostive: isChangePositive
    },
    isLoading: !isLoaded
  }

  const timeTabsProps = {
    activeTab,
    setActiveTab,
    timeTabsOther: timeTabsPrice
  }

  return (
    <div className="chart-container">
      <div className="points-line">
        <DataPoint {...priceDataPoint} />
        <TimeFilterTabs {...timeTabsProps} />
      </div>
      <ResponsiveContainer width={'100%'} height={375}>
        {isLoaded && !isEmptyOrNil(chartData) && isDetailsOpen ? (
          <LineChart
            data={chartData}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            margin={{
              top: 0
            }}>
            <YAxis domain={['auto', 'auto']} hide={true} />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              interval={'preserveStartEnd'}
              allowDataOverflow={false}
            />
            <Tooltip
              content={
                <Label
                  value={formatCurrency(activeData?.usd)}
                  thirdValue={activeData?.exactTime}
                  labels={{ value: 'Price (USD)', thirdValue: 'Time' }}
                />
              }
            />
            <Line dataKey="usd" stroke="#1D41FF" dot={false} strokeWidth={1.5} />
          </LineChart>
        ) : (
          <SimpleSpinner />
        )}
      </ResponsiveContainer>
    </div>
  )
}
